//Assets imports
import { TrashIcon } from "@heroicons/react/outline";
import { DownloadIcon } from "@heroicons/react/solid";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";

import msPowerPointDocIcon from "../../../../../images/icons/powerpoint-document-icon.png";
import msExcelSheetDocIcon from "../../../../../images/icons/excel-sheet-icon.png";
import mswordDocIcon from "../../../../../images/icons/microsoft-word-document-icon.png";
import pdfDocIcon from "../../../../../images/icons/pdf-doc-icon.png";
import documentIcon from "../../../../../images/icons/document-icon.png";
import imageIcon from "../../../../../images/icons/image-icon.svg";
import { formatDate2, getElapsedTime, sanitizeResponseError } from "utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteResourceFiles } from "api/Rover/ApplicationForm";
import {
  IDeleteResourceFilesService,
  IGetResourceFilesServiceRes,
} from "api/Rover/ApplicationForm/types";
import { useDispatch } from "react-redux";
import { notifyUser } from "store/Toast";
import { RoverReactQueryKey } from "constants/ReactQueryKeys";
import Confirm from "components/UI/Modal/Confirm";
import { useState } from "react";
import { usePermission } from "hooks/usePermission";
import { EPermission } from "types/Permission";

export default function ResearchCenterTableRow({ row }: { row: any }) {
  const { applicationFormId } = useParams<string>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const queryClient = useQueryClient();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAsset, setSelectedAsset] =
    useState<IGetResourceFilesServiceRes>();

  const fileTypeIcon: { [key: string]: { icon: any; fileTypes: string[] } } = {
    pdf: { icon: pdfDocIcon, fileTypes: [".pdf", "application/pdf"] },
    msdoc: {
      icon: mswordDocIcon,
      fileTypes: [
        ".doc",
        ".docx",
        ".dotx",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
        "application/msword",
      ],
    },
    mspp: {
      icon: msPowerPointDocIcon,
      fileTypes: [
        ".pptx",
        ".sldx",
        ".ppsx",
        ".potx",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.openxmlformats-officedocument.presentationml.slide",
        "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
        "application/vnd.openxmlformats-officedocument.presentationml.template",
      ],
    },
    msexcel: {
      icon: msExcelSheetDocIcon,
      fileTypes: [
        ".xlsx",
        ".xltx",
        ".csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
        "text/csv",
      ],
    },
    image: {
      icon: imageIcon,
      fileTypes: [
        ".svg",
        ".jpeg",
        ".jpg",
        ".png",
        "image/svg+xml",
        "image/jpeg",
      ],
    },
    default: {
      icon: documentIcon,
      fileTypes: [],
    },
  };

  const fileTypeIconFinder = (contentType: string) => {
    let imgIcon = documentIcon;
    Object.keys(fileTypeIcon).forEach((key) => {
      if (fileTypeIcon[key].fileTypes.includes(contentType)) {
        imgIcon = fileTypeIcon[key].icon;
      }
    });
    return imgIcon;
  };

  const fileTypeFinder = (contentType: string) => {
    let type = "";
    Object.keys(fileTypeIcon).forEach((key) => {
      if (fileTypeIcon[key].fileTypes.includes(contentType)) {
        type = key;
      }
    });
    return type;
  };

  const removeResourceAsset = useMutation(
    (payload: IDeleteResourceFilesService) => deleteResourceFiles(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_RESOURCE]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: "File deleted successfully.",
            toastType: "success",
          })
        );
        setShowDeleteConfirm(false);
      },
      onError: (error) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const handleDelete = (asset: IGetResourceFilesServiceRes) => {
    setSelectedAsset(asset);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedAsset) {
      removeResourceAsset.mutate({
        assetsId: selectedAsset.id,
        applicationFormId: applicationFormId!,
      });
    }
  };

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title="Delete File"
          message={`You are about to delete ${selectedAsset?.name} file. Are you sure ?`}
          acceptButtonLabel="Remove"
          declineButtonLabel="Cancel"
          accept={handleDeleteConfirm}
          decline={() => setShowDeleteConfirm(false)}
        />
      )}
      <tr className="flex items-center w-full justify-between px-5 py-4 text-sm cursor-pointer hover:bg-gray-50">
        <td className="w-72 flex items-center gap-2">
          <div>
            <img
              src={fileTypeIconFinder(row.contentType)}
              className="h-auto w-10 object-fit"
              alt="file-icon"
            />
          </div>
          {row.type === "Folder" && (
            <p
              onClick={() =>
                navigate({
                  search: createSearchParams({
                    folder: row?.name,
                  }).toString(),
                })
              }
              className="hover:underline"
            >
              {row?.name}
            </p>
          )}
          {row.type !== "Folder" && (
            <p className="hover:underline">
              <a href={row.url} target="_blank" rel="noreferrer noopener">
                {row.name}
              </a>
            </p>
          )}
        </td>
        <td className="w-36">{fileTypeFinder(row?.contentType)}</td>

        <td className="w-72 flex justify-between items-center gap-2">
          <div className="flex flex-col">
            <span>
              {getElapsedTime(new Date(row?.createdAt).getTime() / 1000)}
            </span>
            <span className="text-xs text-neutralColor-light italic">
              {new Date(row?.createdAt).toDateString()}
            </span>
          </div>
        </td>
        <td className="w-36">
          <div
            className={`flex gap-4 rounded-full p-1 text-neutralColor-normal top-4 right-2`}
          >
            <div
              id="download"
              className="hover:text-primaryColor-normal transition duration-300 ease-in-out"
            >
              <a href={row?.key} target="_blank" rel="noreferrer noopener">
                <DownloadIcon className="h-5 w-5" />
              </a>
            </div>
            {hasPermission([EPermission.UPDATE_APPLICATION_FORM_RESOURCES]) && (
              <div
                id="delete"
                className="hover:text-tertiaryColor-normal transition duration-300 ease-in-out"
                onClick={() => handleDelete(row)}
              >
                <TrashIcon className="h-5 w-5" />
              </div>
            )}
            {/* <SimpleDropDown
              toggle={<DotsVerticalIcon className='w-5 h-5' />}
              menuItemClassName='z-10'
            >
              {row?.key && (
                <a href={row?.key} target='_blank' rel='noreferrer noopener'>
                  <DropDownItem id='download'>
                    <DownloadIcon className='h-5 w-5' />
                    <span>Download</span>
                  </DropDownItem>
                </a>
              )}
              <DropDownItem id='download' onClick={() => handleDelete(row?.id)}>
                <TrashIcon className='h-5 w-5' />
                <span>Delete</span>
              </DropDownItem>
            </SimpleDropDown> */}
          </div>
        </td>
      </tr>
    </>
  );
}
